import React, { useState, useEffect } from "react";
import { Button, Form, FormInstance, Spin, Tag, Image, Tooltip } from "antd";
import { Check, Info, Warning } from "assets/icons";
import ExclamationMark from "assets/icons/exclamationMarkIcon";
import {
  FleetType,
  IDocumentType,
  IEntryPermissionActive,
  IStepEntryPermissionStatus,
  PlateFormatType,
  entryPermissionActive as entryPermissionActiveDefault,
} from "store/lobby/lobby-model";
import theme from "styles/theme";
import { isDriverLicenseDocumentType } from "utils/documentType";
import { checkPlateFormatType, validationCPF, validationDriverLicense } from "utils/form/validations";
import useTranslation from "utils/hooks/useTranslation";
import CheckDriver from "./../../assets/images/checkDriver.png";
import PlaceholderImage from "./../../assets/images/placeholderImage.png";
import * as S from "./index.styled";

interface IEntryPermission {
  form: FormInstance;
  saleChannelActive: number;
  documentType?: IDocumentType;
  plateFormatType?: PlateFormatType;
  isLoadingEntryPermission: boolean;
  entryPermissionActive: IEntryPermissionActive;
  stepEntryPermissionStatus: IStepEntryPermissionStatus;
  lobbyFetchEntryPermissionAsync: (...args) => void;
  lobbyUpdateStepEntryPermissionStatus: (...args) => void;
  lobbyUpdateEntryPermissionActive: (...args) => void;
}

const CheckReleasedOrBlocked = ({ released }) => {
  const BLOCKED = useTranslation({ id: "blocked" });
  const RELEASED = useTranslation({ id: "check_released" });
  const isReleased = released === 1;
  const Icon = isReleased ? Check : ExclamationMark;
  const label = isReleased ? RELEASED : BLOCKED;

  return (
    <Tag className="status">
      <Icon height="16" width="16" />
      {label}
    </Tag>
  );
};

const EntryPermission = ({
  form,
  documentType,
  plateFormatType,
  saleChannelActive,
  entryPermissionActive,
  isLoadingEntryPermission,
  stepEntryPermissionStatus,
  lobbyFetchEntryPermissionAsync,
  lobbyUpdateEntryPermissionActive,
  lobbyUpdateStepEntryPermissionStatus,
}: IEntryPermission): JSX.Element => {
  const [isDisabled, setIsDisabled] = useState(true);

  const driver1 = Form.useWatch("driver1", form);
  const saleChannelValue = Form.useWatch("saleChannelDescription", form);
  const cpf1 = Form.useWatch("cpf1", form);
  const vehiclePlate = Form.useWatch("vehiclePlate", form);
  const saleChannelId = saleChannelActive || saleChannelValue;

  const eligibleSalesChannelIds = [FleetType.DedicatedFleet, FleetType.Spot, FleetType.Resale, FleetType.AS, FleetType.Insumo, FleetType.InsumoLog];
  const isSalesChannelEligibleToFrotaLegal = typeof saleChannelId === "number" && eligibleSalesChannelIds.includes(saleChannelId);

  const NAME = useTranslation({ id: "name" });
  const PLATE = useTranslation({ id: "plate" });
  const REASON = useTranslation({ id: "reason" });
  const TRY_AGAIN = useTranslation({ id: "try_again" });
  const SITUATION = useTranslation({ id: "situation" });
  const VEHICLE_TYPE = useTranslation({ id: "vehicle_type" });
  const DRIVER_NOT_FOUND = useTranslation({ id: "driver_not_found" });
  const CHECK_INFORMATION = useTranslation({ id: "check_information" });
  const VEHICLE_NOT_FOUND = useTranslation({ id: "vehicle_not_found" });
  const DRIVER_VERIFICATION = useTranslation({ id: "driver_verification" });
  const VEHICLE_VERIFICATION = useTranslation({ id: "vehicle_verification" });
  const LOADING_INFORMATIONS = useTranslation({ id: "loading_informations" });
  const INFORMATION_VERIFICATION = useTranslation({ id: "information_verification" });
  const UNABLE_TO_RETRIEVE_INFORMATION = useTranslation({ id: "unable_to_retrieve_information" });
  const YOU_CAN_STILL_PROCEED_WITH_VEHICLE_ENTRY = useTranslation({ id: "you_can_still_proceed_with_vehicle_entry" });
  const PLEASE_WAIT_WHILE_WE_RETRIEVE_THE_DRIVERS_INFORMATION = useTranslation({ id: "please_wait_while_we_retrieve_the_drivers_information" });
  const NO_INFORMATION_COULD_BE_FOUND_IN_THE_FROTA_LEGAL_SYSTEM = useTranslation({ id: "no_information_could_be_found_in_the_Frota_Legal_system" });
  const WARNING_AVAILABLE_FOR_THE_SELECTED_SALES_CHANNEL = useTranslation({ id: "warning_that_it_is_not_available_for_the_selected_sales_channel" });
  const VALIDATE_DRIVER_AND_VEHICLE_INFORMATION_BEFORE_REGISTERING_ENTRY = useTranslation({ id: "validate_driver_and_vehicle_information_before_registering_entry" });

  useEffect(() => {
    const hasDriver1 = driver1 && driver1.length > 0;
    const hasCpf1 = cpf1 && (isDriverLicenseDocumentType(documentType) ? validationDriverLicense.test(cpf1) : validationCPF.test(cpf1));
    const validationPlateRegex = checkPlateFormatType(plateFormatType);
    const hasPlate = vehiclePlate && validationPlateRegex.test(vehiclePlate);
    setIsDisabled(!(hasDriver1 && hasCpf1 && hasPlate && isSalesChannelEligibleToFrotaLegal));
    if (hasPlate && hasCpf1 && hasDriver1 && saleChannelId) {
      lobbyUpdateStepEntryPermissionStatus(IStepEntryPermissionStatus.CheckDriverAndVehicle);
      lobbyUpdateEntryPermissionActive(entryPermissionActiveDefault);
    }
    if (!driver1 && !cpf1 && !saleChannelId) {
      lobbyUpdateStepEntryPermissionStatus(IStepEntryPermissionStatus.CheckDriverAndVehicle);
      lobbyUpdateEntryPermissionActive(entryPermissionActiveDefault);
    }
  }, [driver1, cpf1, vehiclePlate, saleChannelId, plateFormatType, documentType]);

  const handleCheckInformation = (vehiclePlate, cpf1, saleChannelId) => {
    const identificationDocument = cpf1;
    lobbyFetchEntryPermissionAsync(vehiclePlate, identificationDocument, saleChannelId);
  };

  const convertImage = (imageBase64: string): string => {
    if (!imageBase64) {
      return PlaceholderImage;
    }
    if (imageBase64.startsWith("data:image/")) {
      return imageBase64;
    }
    return `data:image/png;base64,${imageBase64}`;
  };

  const renderDriverAndVehicleCheck = () => {
    const CHECK_DRIVER_AND_VEHICLE = 0;
    const LOADING_CHECK_DRIVER_AND_VEHICLE = 1;
    const VERIFIED_DRIVER_AND_VEHICLE = 2;
    const NOT_FOUND_DRIVER_AND_VEHICLE = 3;
    const RETRY_CHECK_DRIVER_AND_VEHICLE = 4;

    switch (stepEntryPermissionStatus) {
      case RETRY_CHECK_DRIVER_AND_VEHICLE:
        return (
          <S.DriverAndVehicleCheckContainer>
            <S.Title>{INFORMATION_VERIFICATION}</S.Title>
            <S.CheckContainer>
              <Warning width="44" height="44" fill="#000" />
              <S.TitleDriverCheck>{UNABLE_TO_RETRIEVE_INFORMATION}</S.TitleDriverCheck>
              <Button className="btn-check" onClick={() => handleCheckInformation(vehiclePlate, cpf1, saleChannelId)}>
                {TRY_AGAIN}
              </Button>
            </S.CheckContainer>
          </S.DriverAndVehicleCheckContainer>
        );
      case NOT_FOUND_DRIVER_AND_VEHICLE:
        return (
          <S.DriverAndVehicleCheckContainer>
            <S.Title>{INFORMATION_VERIFICATION}</S.Title>
            <S.CheckContainer>
              <Info width="44" height="44" />
              <S.TitleDriverCheck>{NO_INFORMATION_COULD_BE_FOUND_IN_THE_FROTA_LEGAL_SYSTEM}</S.TitleDriverCheck>
              <S.DescriptionNameDriverCheck>{YOU_CAN_STILL_PROCEED_WITH_VEHICLE_ENTRY}</S.DescriptionNameDriverCheck>
            </S.CheckContainer>
          </S.DriverAndVehicleCheckContainer>
        );
      case LOADING_CHECK_DRIVER_AND_VEHICLE:
        return (
          <S.DriverAndVehicleCheckContainer>
            <S.Title>{INFORMATION_VERIFICATION}</S.Title>
            <S.CheckContainer>
              <Spin spinning={isLoadingEntryPermission} className="spin" />
              <S.TitleDriverCheck>{LOADING_INFORMATIONS}</S.TitleDriverCheck>
              <S.DescriptionNameDriverCheck>{PLEASE_WAIT_WHILE_WE_RETRIEVE_THE_DRIVERS_INFORMATION}</S.DescriptionNameDriverCheck>
            </S.CheckContainer>
          </S.DriverAndVehicleCheckContainer>
        );
      case CHECK_DRIVER_AND_VEHICLE:
        return (
          <S.DriverAndVehicleCheckContainer>
            <S.Title>{INFORMATION_VERIFICATION}</S.Title>
            <S.CheckContainer>
              <img src={CheckDriver} alt="" />
              <S.TitleDriverCheck>{INFORMATION_VERIFICATION}</S.TitleDriverCheck>
              <S.DescriptionNameDriverCheck>
                {VALIDATE_DRIVER_AND_VEHICLE_INFORMATION_BEFORE_REGISTERING_ENTRY}
                {saleChannelValue && !isSalesChannelEligibleToFrotaLegal && (
                  <>
                    <br />
                    <br />
                    <div style={{ color: theme["@color-danger-300"], fontWeight: theme["@medium-font-weight"] }}>{WARNING_AVAILABLE_FOR_THE_SELECTED_SALES_CHANNEL}</div>
                  </>
                )}
              </S.DescriptionNameDriverCheck>
              <Button className="btn-check" disabled={isDisabled} onClick={() => handleCheckInformation(vehiclePlate, cpf1, saleChannelId)}>
                {CHECK_INFORMATION}
              </Button>
            </S.CheckContainer>
          </S.DriverAndVehicleCheckContainer>
        );
      case VERIFIED_DRIVER_AND_VEHICLE:
        return (
          <div style={{ marginTop: "13px" }}>
            <S.DriverCheckContainer released={entryPermissionActive?.driver?.qualification}>
              <S.DriverImage>
                <S.Title>{DRIVER_VERIFICATION}</S.Title>
                <div style={{ backgroundColor: "#FAFAFA" }}>
                  <Image
                    src={convertImage(entryPermissionActive?.driver?.photoBase64)}
                    preview={!!entryPermissionActive?.driver?.photoBase64}
                    style={{ objectFit: "contain" }}
                    alt="driverImage"
                    width={269}
                    height={168}
                  />
                </div>
              </S.DriverImage>
              {entryPermissionActive?.driver?.isFound ? (
                <div>
                  <S.DriverContainer>
                    <S.DescriptionTitle>{NAME}</S.DescriptionTitle>
                    <S.DescriptionName>{entryPermissionActive?.driver?.name ?? "-"}</S.DescriptionName>
                  </S.DriverContainer>
                  <S.DriverContainer>
                    <S.DescriptionTitle>{SITUATION}</S.DescriptionTitle>
                    <CheckReleasedOrBlocked released={entryPermissionActive?.driver?.qualification} />
                  </S.DriverContainer>
                  <S.DriverContainer>
                    <S.DescriptionTitle>{REASON}</S.DescriptionTitle>
                    <Tooltip
                      title={entryPermissionActive?.driver?.reason && entryPermissionActive?.driver?.reason.length > 32 ? entryPermissionActive?.driver?.reason : undefined}
                      placement="top"
                    >
                      <S.DescriptionName>{entryPermissionActive?.driver?.reason ?? "-"}</S.DescriptionName>
                    </Tooltip>
                  </S.DriverContainer>
                </div>
              ) : (
                <S.DriverContainer>
                  <Warning height={24} width={24} fill={theme["@color-warning-300"]} /> <S.DescriptionName>{DRIVER_NOT_FOUND}</S.DescriptionName>
                </S.DriverContainer>
              )}
            </S.DriverCheckContainer>

            <S.DriverCheckContainer released={entryPermissionActive?.vehicle?.qualification}>
              <S.DriverImage>
                <S.Title>{VEHICLE_VERIFICATION}</S.Title>
                <div style={{ backgroundColor: "#FAFAFA" }}>
                  <Image
                    src={convertImage(entryPermissionActive?.vehicle?.photoBase64)}
                    style={{ objectFit: "contain" }}
                    alt="vehicleImage"
                    width={269}
                    height={168}
                    preview={!!entryPermissionActive?.vehicle?.photoBase64}
                  />
                </div>
              </S.DriverImage>
              {entryPermissionActive?.vehicle?.isFound ? (
                <div>
                  <S.DriverContainer>
                    <S.DescriptionTitle>{VEHICLE_TYPE}</S.DescriptionTitle>
                    <S.DescriptionName>{entryPermissionActive?.vehicle?.description ?? "-"}</S.DescriptionName>
                  </S.DriverContainer>
                  <S.VehicleContainer>
                    <div>
                      <S.DescriptionTitle>{SITUATION}</S.DescriptionTitle>
                      <CheckReleasedOrBlocked released={entryPermissionActive?.vehicle?.qualification} />
                    </div>
                    <div style={{ display: "grid" }}>
                      <S.DescriptionTitle>{PLATE}</S.DescriptionTitle>
                      <S.DescriptionName>{entryPermissionActive?.vehicle?.plate?.toUpperCase() ?? "-"}</S.DescriptionName>
                    </div>
                  </S.VehicleContainer>
                  <S.DriverContainer>
                    <S.DescriptionTitle>{REASON}</S.DescriptionTitle>
                    <Tooltip
                      title={entryPermissionActive?.vehicle?.reason && entryPermissionActive?.vehicle?.reason.length > 32 ? entryPermissionActive?.vehicle?.reason : undefined}
                      placement="top"
                    >
                      <S.DescriptionName>{entryPermissionActive?.vehicle?.reason ?? "-"}</S.DescriptionName>
                    </Tooltip>
                  </S.DriverContainer>
                </div>
              ) : (
                <S.DriverContainer>
                  <Warning height={24} width={24} fill={theme["@color-warning-300"]} /> <S.DescriptionName>{VEHICLE_NOT_FOUND}</S.DescriptionName>
                </S.DriverContainer>
              )}
            </S.DriverCheckContainer>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="divider" />
      {renderDriverAndVehicleCheck()}
    </>
  );
};

export default EntryPermission;
